import { Navigate, Outlet } from 'react-router-dom';

import PrivateRoute from '../components/PrivateRoute/PrivateRoute';
import PublicRoute from '../components/PublicRoute/PublicRoute';
import { ROUTES } from '../constants/routes';
import { AuthorizationLayout } from '../layouts/AuthorizationLayout/AuthorizationLayout';
import { CabinetLayout } from '../layouts/CabinetLayout/CabinetLayout';
import { DashboardV2Layout } from '../layouts/DashboardV2Layout/DashboardV2Layout';
import { Cabinet } from '../pages/Cabinet/Cabinet';
import { LoginPage } from '../pages/auth/LoginPage/LoginPage';
import { ResetPassword } from '../pages/auth/ResetPassword/ResetPassword';
import { Station } from '../pages/Station/Station';
import { TermsOfUsage } from '../pages/auth/TermsOfUsage/TermsOfUsage';
import { UserHistory } from '../pages/UserHistory/UserHistory';
import AdminRoute from '../components/AdminRoute/AdminRoute';
import { NorFound } from '../pages/NotFound/NotFound';
import { UnavailableServicePage } from '../pages/UnavailableServicePage/UnavailableServicePage';
import { DiscountProvider } from '../context/discountContext';
import { LoyaltyPage } from '../pages/Loyalty/Loyalty';
import { MapPage } from '../pages/Map/Map';
import { StationsList } from '../pages/StationsList/StationsList';
import { Profile } from '../pages/Profile/Profile';
import { StationRequest } from '../pages/StationsRequest/StationRequest';
import { StationsV2 } from '../pages/DashboardV2/Stations/Stations';
import { SessionsV2 } from '../pages/DashboardV2/Sessions/Sessions';
import { SessionMetrics as SessionMetricsV2 } from '../pages/DashboardV2/Sessions/SessionMetrics';
import { TransactionsV2 } from '../pages/DashboardV2/Transactions/Transactions';
import { UsersV2 } from '../pages/DashboardV2/Users/Users';
import { MetricsV2 } from '../pages/DashboardV2/Metrics/Metrics';
import { CreateStation } from '../pages/DashboardV2/CreateStation/CreateStation';
import { StationDetails as StationDetailsV2 } from '../pages/DashboardV2/StationDetails/StationDetails';
import { UserDetails as UserDetailsV2 } from '../pages/DashboardV2/UserDetails/UserDetails';
import { RolesV2 } from '../pages/DashboardV2/Roles/Roles';
import { CreateRole as CreateRoleV2 } from '../pages/DashboardV2/Roles/CreateRole';
import { EditRole as EditRoleV2 } from '../pages/DashboardV2/Roles/EditRole';
import { OrganizationsV2 } from '../pages/DashboardV2/Organizations/Organizations';
import { TeamV2 } from '../pages/DashboardV2/Team/Team';
import { OrganizationPremiumV2, PremiumV2 } from '../pages/DashboardV2/Premium/Premium';
import { OrganizationStations } from '../pages/DashboardV2/Organizations/OrganizationStations/OrganizationStations';
import { OrganizationEditor } from '../pages/DashboardV2/Organizations/OrganizationEditor';
import { NotificationsV2 } from '../pages/DashboardV2/Notifications/Notifications';
import { StationRequestsV2 } from '../pages/DashboardV2/StationRequests/StationRequests';
import { SettingsV2 } from '../pages/DashboardV2/Settings/Settings';
import { SystemV2 } from '../pages/DashboardV2/System/System';
import { ProfileV2 } from '../pages/DashboardV2/Profile/Profile';
import { SectionTabRoute } from '../pages/DashboardV2/components/SectionTabs';
import {
  ORGANIZATIONS_ROLES_SECTION,
  SUPPORT_SECTION,
} from '../layouts/DashboardV2Layout/dashboardSections';
import { Support } from '../pages/DashboardV2/Support/Support';
import { TicketDetail } from '../pages/DashboardV2/Support/TicketDetail';
import { KnowledgeBase } from '../pages/DashboardV2/KnowledgeBase/KnowledgeBase';
import { CabinetNotifications } from '../pages/CabinetNotifications/CabinetNotifications';
import { ErrorBoundary } from '../components/common/ErrorBoundary/ErrorBoundary';
import { Landing } from '../pages/Landing/Landing';

export const routes = [
  {
    path: '/',
    children: [
      {
        path: '',
        element: (
          <PublicRoute>
            <Outlet />
          </PublicRoute>
        ),
      },
      {
        // Public marketing page; deliberately outside PublicRoute, which sends
        // anyone holding a token straight to the cabinet.
        path: ROUTES.LANDING,
        element: <Landing />,
      },
      {
        path: ROUTES.LOGIN,
        element: (
          <PublicRoute>
            <AuthorizationLayout>
              <LoginPage />
            </AuthorizationLayout>
          </PublicRoute>
        ),
      },
      {
        // Where the organisation-facing landing page and the dashboard send
        // owners: the same sign-in screen as /login, under the resource it
        // belongs to.
        path: ROUTES.ORGANISATION_LOGIN,
        element: (
          <PublicRoute>
            <AuthorizationLayout>
              <LoginPage />
            </AuthorizationLayout>
          </PublicRoute>
        ),
      },
      {
        // The URL this used to live at, kept so owner bookmarks still resolve.
        path: ROUTES.LOGIN_OWNER,
        element: <Navigate to={`/${ROUTES.ORGANISATION_LOGIN}`} replace />,
      },
      {
        path: ROUTES.RESET_PASSWORD,
        element: (
          <AuthorizationLayout>
            <ResetPassword />
          </AuthorizationLayout>
        ),
      },
      {
        path: ROUTES.TERMS_OF_USAGE,
        element: (
          <AuthorizationLayout>
            <TermsOfUsage />
          </AuthorizationLayout>
        ),
      },
      {
        path: ROUTES.CABINET,
        element: (
          <PrivateRoute>
            <CabinetLayout>
              <DiscountProvider>
                <Outlet />
              </DiscountProvider>
            </CabinetLayout>
          </PrivateRoute>
        ),
        children: [
          { path: '', element: <Cabinet /> },
          { path: `${ROUTES.CAB_STATION}/:id/:connectorId?`, element: <Station /> },
          { path: ROUTES.CAB_SESSIONS, element: <UserHistory /> },
          { path: ROUTES.LOYALTY, element: <LoyaltyPage /> },
          { path: ROUTES.MAP, element: <MapPage /> },
          { path: ROUTES.STATIONS_LIST, element: <StationsList /> },
          { path: ROUTES.CAB_TRANSACTIONS, element: <UserHistory /> },
          { path: ROUTES.CAB_PROFILE, element: <Profile /> },
          {
            path: ROUTES.STATIONS_REQUEST,
            element: <StationRequest />,
          },
          {
            path: ROUTES.NOTIFICATIONS,
            element: <ErrorBoundary><CabinetNotifications /></ErrorBoundary>,
          },
        ],
      },
      {
        path: ROUTES.DASHBOARD,
        element: (
          <AdminRoute>
            <DashboardV2Layout />
          </AdminRoute>
        ),
        children: [
          { path: '', element: <ErrorBoundary><MetricsV2 /></ErrorBoundary> },
          { path: ROUTES.METRICS, element: <ErrorBoundary><MetricsV2 /></ErrorBoundary> },
          { path: ROUTES.DASH_STATIONS, element: <ErrorBoundary><StationsV2 /></ErrorBoundary> },
          { path: ROUTES.DASH_STATION_DETAILS, element: <ErrorBoundary><StationDetailsV2 /></ErrorBoundary> },
          { path: ROUTES.DASH_SESSIONS, element: <ErrorBoundary><SessionsV2 /></ErrorBoundary> },
          { path: ROUTES.DASH_SESSION_METRICS, element: <ErrorBoundary><SessionMetricsV2 /></ErrorBoundary> },
          { path: ROUTES.DASH_TRANSACTIONS, element: <ErrorBoundary><TransactionsV2 /></ErrorBoundary> },
          { path: ROUTES.DASH_USERS, element: <ErrorBoundary><UsersV2 /></ErrorBoundary> },
          { path: ROUTES.DASH_USER_DETAILS, element: <ErrorBoundary><UserDetailsV2 /></ErrorBoundary> },
          { path: ROUTES.DASH_ROLES, element: <ErrorBoundary><SectionTabRoute section={ORGANIZATIONS_ROLES_SECTION}><RolesV2 /></SectionTabRoute></ErrorBoundary> },
          { path: ROUTES.DASH_ROLE_CREATE, element: <ErrorBoundary><CreateRoleV2 /></ErrorBoundary> },
          { path: ROUTES.DASH_ROLE_EDIT, element: <ErrorBoundary><EditRoleV2 /></ErrorBoundary> },
          { path: ROUTES.DASH_ORGANIZATIONS, element: <ErrorBoundary><SectionTabRoute section={ORGANIZATIONS_ROLES_SECTION}><OrganizationsV2 /></SectionTabRoute></ErrorBoundary> },
          { path: ROUTES.DASH_ORGANIZATION_CREATE, element: <ErrorBoundary><SectionTabRoute section={ORGANIZATIONS_ROLES_SECTION}><OrganizationEditor /></SectionTabRoute></ErrorBoundary> },
          { path: ROUTES.DASH_ORGANIZATION_EDIT, element: <ErrorBoundary><OrganizationEditor /></ErrorBoundary> },
          { path: ROUTES.DASH_TEAM, element: <ErrorBoundary><TeamV2 /></ErrorBoundary> },
          { path: ROUTES.DASH_PREMIUM, element: <ErrorBoundary><PremiumV2 /></ErrorBoundary> },
          { path: ROUTES.DASH_ORGANIZATION_PREMIUM, element: <ErrorBoundary><OrganizationPremiumV2 /></ErrorBoundary> },
          { path: ROUTES.DASH_ORGANIZATION_STATIONS, element: <ErrorBoundary><OrganizationStations /></ErrorBoundary> },
          { path: ROUTES.DASH_ORGANIZATION_TEAM, element: <ErrorBoundary><TeamV2 /></ErrorBoundary> },
          { path: ROUTES.CREATE_STATION, element: <ErrorBoundary><CreateStation /></ErrorBoundary> },
          { path: ROUTES.NOTIFICATIONS, element: <ErrorBoundary><NotificationsV2 /></ErrorBoundary> },
          { path: ROUTES.DASH_STATION_REQUESTS, element: <ErrorBoundary><SectionTabRoute section={SUPPORT_SECTION}><StationRequestsV2 /></SectionTabRoute></ErrorBoundary> },
          { path: ROUTES.DASH_SETTINGS, element: <ErrorBoundary><SettingsV2 /></ErrorBoundary> },
          { path: ROUTES.DASH_SYSTEM, element: <ErrorBoundary><SystemV2 /></ErrorBoundary> },
          { path: ROUTES.DASH_PROFILE, element: <ErrorBoundary><ProfileV2 /></ErrorBoundary> },
          { path: ROUTES.DASH_SUPPORT, element: <ErrorBoundary><SectionTabRoute section={SUPPORT_SECTION}><Support /></SectionTabRoute></ErrorBoundary> },
          { path: ROUTES.DASH_SUPPORT_TICKET, element: <ErrorBoundary><SectionTabRoute section={SUPPORT_SECTION}><TicketDetail /></SectionTabRoute></ErrorBoundary> },
          { path: ROUTES.DASH_KNOWLEDGE_BASE, element: <ErrorBoundary><SectionTabRoute section={SUPPORT_SECTION}><KnowledgeBase /></SectionTabRoute></ErrorBoundary> },
        ],
      },
      {
        path: ROUTES.SERVICE_UNAVAILABLE,
        element: <UnavailableServicePage />,
      },
      {
        path: '*',
        element: (
          <CabinetLayout>
            <NorFound />
          </CabinetLayout>
        ),
      },
    ],
  },
];
